<template>
  <div class="fixed top-0 right-0 left-0 z-20">
    <header class="w-full border-t-4 border-yellow-400 shadow-lg">
      <div class="hidden border-b border-neutral-900 bg-neutral-950 lg:block">
        <div
          class="mx-auto flex w-full max-w-[var(--breakpoint-2xl)] items-center justify-between overflow-x-auto px-6 sm:px-8"
        >
          <UNavigationMenu
            :items="topNavigationMenu"
            variant="link"
            class="-ml-3"
            :ui="{
              link: 'font-bold',
            }"
          />
          <div v-if="bannerEnabled" class="hidden w-full text-right lg:block">
            <NuxtLink
              v-if="story.content.banner_link.linktype === 'story'"
              :to="`/${story.content.banner_link.cached_url}`"
              class="text-sm font-semibold text-yellow-400 hover:underline"
            >
              {{ story.content.banner_copy }}
            </NuxtLink>
            <NuxtLink
              v-else
              :to="story.content.banner_link.url"
              class="text-sm font-semibold text-yellow-400 hover:underline"
            >
              {{ story.content.banner_copy }}
            </NuxtLink>
          </div>
        </div>
      </div>
      <div class="border-b-2 border-neutral-900 bg-black">
        <div
          class="mx-auto w-full max-w-[var(--breakpoint-2xl)] px-6 py-4 sm:px-8"
        >
          <div class="flex items-center">
            <NuxtLink to="/" class="shrink-0">
              <NuxtImg
                provider="storyblok"
                class="h-16 w-auto"
                height="64"
                width="74"
                :src="story.content.site_logo.filename"
                :alt="story.content.site_logo.alt"
                preload
                priority
                loading="eager"
              />
            </NuxtLink>
            <UNavigationMenu
              v-model="active"
              :items="mainNavigationMenu"
              variant="link"
              :ui="{
                linkLeadingIcon: 'group-hover:text-white',
                link: 'group text-md font-bold px-2.5 py-2.5 pt-3.5 border-b-2 border-black hover:border-yellow-400 hover:text-white',
                viewport: 'bg-neutral-900 max-w-[var(--breakpoint-md)]',
                childLinkDescription: 'line-clamp-2',
              }"
              class="mx-auto hidden justify-center lg:flex"
            >
              <template
                #default="{
                  /* item, open */
                }"
              >
                <!-- Default trigger rendering (or customize trigger here if needed) -->
              </template>

              <template #slot-content="{ item }: { item: NavigationMenuItem }">
                <div class="grid gap-4 p-2 lg:grid-cols-5">
                  <!-- Slot Content Column -->
                  <NuxtLink
                    v-if="item.heading || item.image || item.lead"
                    :to="
                      item.link.linktype === 'story'
                        ? `/${item.link.cached_url}`
                        : item.link.url
                    "
                    class="group col-span-2"
                  >
                    <UCard
                      class="relative isolate h-full bg-black"
                      :ui="{ root: 'overflow-hidden rounded-xl' }"
                    >
                      <NuxtImg
                        v-if="item.image"
                        provider="storyblok"
                        :src="item.image.filename"
                        :alt="item.image.alt"
                        class="absolute inset-0 h-full w-full object-cover transition-all duration-500 group-hover:scale-102"
                        sizes="xs:100svw sm:100svw md:50svw lg:50svw xl:50svw xxl:50svw"
                        :modifiers="{
                          smart: true,
                          fit: 'cover',
                          filters: {
                            focal: item?.image?.focus ?? undefined,
                          },
                        }"
                        placeholder
                        loading="lazy"
                      />
                      <div
                        class="absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-neutral-950/75 to-neutral-950"
                      />
                      <div
                        class="absolute right-4 bottom-4 left-4 z-[2] space-y-2"
                      >
                        <p
                          v-if="item.subheading"
                          class="-mb-0 text-xs font-bold text-yellow-400 uppercase drop-shadow-lg"
                        >
                          {{ item.subheading }}
                        </p>
                        <h3
                          v-if="item.heading"
                          class="text-lg leading-tight font-bold text-white drop-shadow-lg"
                        >
                          {{ item.heading }}
                        </h3>
                        <UButton
                          v-if="item.link"
                          :to="
                            item.link.linktype === 'story'
                              ? `/${item.link.cached_url}`
                              : item.link.url
                          "
                          variant="subtle"
                          size="sm"
                          class="w-full justify-center"
                          trailing-icon="i-lucide-chevron-right"
                        >
                          Learn More
                        </UButton>
                      </div>
                    </UCard>
                  </NuxtLink>
                  <ul class="space-y-2 lg:col-span-3">
                    <li
                      v-for="child in item.children?.slice(-3) ?? []"
                      :key="child.label"
                      class="group col-span-1 w-full lg:col-span-2"
                    >
                      <NuxtLink
                        :to="child.to"
                        class="flex w-full cursor-pointer gap-2 rounded-xl p-2 text-left text-sm transition-colors hover:bg-(--ui-bg-elevated)/50"
                      >
                        <UIcon
                          :name="child.icon"
                          class="size-5 shrink-0 text-neutral-500 group-hover:text-(--ui-text)"
                        />
                        <div>
                          <p class="font-semibold text-(--ui-text)">
                            {{ child.label }}
                          </p>
                          <p class="line-clamp-2 text-(--ui-text-muted)">
                            {{ child.description }}
                          </p>
                        </div>
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
              </template>

              <!-- Other items without a specific slot will use default rendering -->
            </UNavigationMenu>
            <div
              class="ml-auto flex shrink-0 items-center justify-center space-x-2 lg:ml-0"
            >
              <LazyMarketingNavigationSearch />
              <div class="lg:hidden">
                <USlideover
                  v-model:open="mobileOpen"
                  title="Live For Today"
                  :close="{
                    color: 'primary',
                    variant: 'outline',
                    class: 'rounded-full bg-black z-[1]',
                  }"
                  :ui="{
                    content: 'divide-y-0 bg-neutral-950/90 backdrop-blur-3xl',
                  }"
                >
                  <UButton
                    title="Menu"
                    size="xl"
                    variant="subtle"
                    icon="i-lucide-menu"
                    class="cursor-pointer"
                  />
                  <template #body>
                    <UNavigationMenu
                      v-model="active"
                      :items="mainNavigationMenu"
                      orientation="vertical"
                      :ui="{
                        link: 'text-md font-semibold px-3.5 py-2.5',
                      }"
                    />
                    <UNavigationMenu
                      v-model="active"
                      :items="topNavigationMenu"
                      orientation="vertical"
                      :ui="{
                        link: 'text-md font-semibold px-3.5 py-2.5',
                      }"
                    />
                  </template>
                  <template #footer>
                    <UButton
                      size="xl"
                      class="w-full cursor-pointer justify-center text-center"
                      @click="bookNow"
                    >
                      Book Now
                    </UButton>
                  </template>
                </USlideover>
              </div>
              <UButton
                size="xl"
                class="hidden cursor-pointer lg:block"
                @click="bookNow"
              >
                Book Now
              </UButton>
            </div>
          </div>
        </div>
      </div>
    </header>
  </div>
</template>

<script setup lang="ts">
import type {
  NavigationLinkStoryblok,
  NavigationChildrenStoryblok,
  NavigationSlotStoryblok,
} from '~/types/component-types-sb';
import type { NavigationMenuItem } from '#ui/types';

const isPreview = useRuntimeConfig().public.NODE_ENV !== 'production';
const mobileOpen = ref(false);

const story = await useAsyncStoryblok('config', {
  version: isPreview ? 'draft' : 'published',
});

// Active Navigation Route
const active = ref<string>();

// Navigation Menus
const mainNavigationMenu = ref<NavigationMenuItem[]>([]);
const topNavigationMenu = ref<NavigationMenuItem[]>([]);

// Format Navigation menu Items
function processMenuData(
  menuData: (
    | NavigationLinkStoryblok
    | NavigationChildrenStoryblok
    | NavigationSlotStoryblok
  )[]
): NavigationMenuItem[] {
  return menuData.map((item) => {
    const newItem: NavigationMenuItem = {
      label: item.label || '',
      icon: item.icon || undefined,
    };

    if ('slot' in item && item.slot) {
      newItem.slot = item.slot;
      newItem.image = item.image;
      newItem.heading = item.heading;
      newItem.lead = item.lead;
      newItem.subheading = item.subheading;
      newItem.link = item.link;
    }

    if (item.to) {
      let url = '';
      if (item.to.linktype === 'story') {
        url = `/${item.to.cached_url}`;
      } else {
        url = item.to.url;
      }
      // Remove trailing slashes
      url = url.replace(/\/+$/, '');
      newItem.to = url;
    }

    if ('description' in item && item.description) {
      newItem.description = item.description;
    }

    if ('children' in item && item.children && item.children.length > 0) {
      newItem.children = processMenuData(item.children);
    }

    return newItem;
  });
}

if (story.value && story.value.content) {
  mainNavigationMenu.value = processMenuData(story.value.content.main_nav);
  topNavigationMenu.value = processMenuData(story.value.content.top_nav);
}

const route = useRoute();
const router = useRouter();

const bookNow = () => {
  if (route.path.includes('/activities/')) {
    router.replace({
      query: {
        ...route.query,
        tab: 'availability',
      },
    });
  } else if (route.path.includes('/events/')) {
    router.replace({
      query: {
        ...route.query,
        tab: 'rsvp',
      },
    });
  } else if (route.path.includes('/groups/')) {
    router.replace({
      query: {
        ...route.query,
        tab: 'packages',
      },
    });
  } else {
    navigateTo('/book');
  }
};

const bannerEnabled = computed(() => {
  if (!story.value?.content?.start_date || !story.value?.content?.end_date) {
    return false;
  }

  const currentDate = new Date();
  const startDate = new Date(story.value.content.start_date);
  const endDate = new Date(story.value.content.end_date);

  if (startDate > currentDate || endDate < currentDate) {
    return false;
  } else {
    return true;
  }
});

// Watch for route changes to close the mobile menu
watch(
  () => route.path,
  () => {
    mobileOpen.value = false;
  }
);
</script>
